import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { FINANCE_NAV } from './nav';

interface InvoiceRow { inv_code: string; inv_name: string; inv_date: string | null; issued_by: string | null; paid_amount: number; age_days: number; status: string; }

const STATUS_COLORS: Record<string, string> = { paid: 'success', unpaid: 'warning', overdue: 'danger' };

const Invoicing: React.FC = () => {
    const { api } = useInternalApi();
    const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');

    useEffect(() => { document.title = 'Invoicing & Dunning - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('admin-dashboard/finance/invoicing/').then(res => {
            if (res.data?.status === 'success') {
                setInvoices(res.data.data.invoices || []);
                setSummary(res.data.data.summary);
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = filter ? invoices.filter(i => i.status === filter) : invoices;

    return (
        <InternalShell activeDashboard="finance_center" subNav={FINANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2 text-primary" />Invoicing & Dunning</h2>
            <p className="text-muted mb-4">Every invoice, with real paid/unpaid/overdue status computed from payment history</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    {summary && (
                        <div className="row mb-4">
                            {[
                                { key: '', label: 'Total', value: summary.total, color: 'secondary' },
                                { key: 'paid', label: 'Paid', value: summary.paid, color: 'success' },
                                { key: 'unpaid', label: 'Unpaid', value: summary.unpaid, color: 'warning' },
                                { key: 'overdue', label: 'Overdue (30d+)', value: summary.overdue, color: 'danger' },
                            ].map(s => (
                                <div className="col-md-3 mb-3" key={s.label}>
                                    <div className={`card border-0 shadow-sm ${filter === s.key ? 'border-primary' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setFilter(s.key)}>
                                        <div className="card-body">
                                            <div className="text-muted small mb-1">{s.label}</div>
                                            <h4 className={`text-${s.color}`}>{s.value}</h4>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {filtered.length === 0 ? (
                                <div className="text-center py-5 text-muted">No invoices found.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Invoice</th><th>Issued By</th><th>Date</th><th>Age (days)</th><th>Paid</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {filtered.map(i => (
                                            <tr key={i.inv_code}>
                                                <td>{i.inv_name}<div className="small text-muted">{i.inv_code}</div></td>
                                                <td>{i.issued_by || '—'}</td>
                                                <td>{i.inv_date ? new Date(i.inv_date).toLocaleDateString() : '—'}</td>
                                                <td>{i.age_days}</td>
                                                <td>{i.paid_amount.toLocaleString()}</td>
                                                <td><span className={`badge bg-${STATUS_COLORS[i.status]}`}>{i.status}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default Invoicing;
